"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { imageForCategory } from "@/lib/categoryImages";
import { getCategoryUrl, getSubcategoryUrl } from "@/lib/categoryUrls";

const CATEGORY_TERMS: Record<string, { label: string; cta: string }> = {
  "fantasy-names": {
    label: "Mythic Archetypes",
    cta: "Explore Mythic Races & Species",
  },
  "real-names": {
    label: "Cultural Traditions",
    cta: "Browse World & Historical Origins",
  },
  "places-locations": {
    label: "Realms & Locales",
    cta: "Explore Kingdoms, Cities & Lands",
  },
  "pop-culture": {
    label: "Universes & Lore",
    cta: "Browse Tabletop, Gaming & Fiction",
  },
  "other-names": {
    label: "Concepts & Relics",
    cta: "Explore Spells, Guilds & Relics",
  },
  "description-generators": {
    label: "Worldbuilding Tools",
    cta: "Explore Story & Character Lore",
  },
};

interface CategoryProps {
  category: {
    name: string;
    slug: string;
    icon?: string;
    description?: string;
    subcategories?: Array<{ name: string; slug: string; names?: any[] }>;
  };
  index?: number;
}

export default function CategoryCard({ category, index = 0 }: CategoryProps) {
  const subCount = category.subcategories?.length || 0;
  const meta = CATEGORY_TERMS[category.slug] || {
    label: "Curated Themes",
    cta: "Explore All Collections",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -5 }}
      className="h-full"
    >
      <Card className="h-full flex flex-col justify-between overflow-hidden rounded-3xl border border-border/80 bg-card/90 hover:border-primary/50 shadow-md hover:shadow-2xl transition-all duration-300 group p-0">
        <div>
          {/* Visual Header with Overlay */}
          <div className="relative h-36 sm:h-40 w-full overflow-hidden">
            <Image
              src={imageForCategory(category.slug)}
              alt={`${category.name} realm atmosphere`}
              fill
              sizes="(max-width: 768px) 100vw, 400px"
              className="object-cover transition-transform duration-700 group-hover:scale-108 brightness-95"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
            
            {/* Thematic Icon Badge */}
            <div className="absolute top-3.5 left-4 h-12 w-12 rounded-2xl bg-background/90 backdrop-blur-md border border-border/70 flex items-center justify-center text-2xl shadow-md group-hover:scale-110 transition-transform duration-300">
              {category.icon || "✨"}
            </div>

            {/* Counter Badge */}
            <Badge variant="fantasy" className="absolute top-3.5 right-4 text-xs shadow-md">
              {meta.label}
            </Badge>
          </div>

          {/* Body Content */}
          <div className="px-6 pt-3">
            <h3 className="text-xl font-bold font-fantasy text-foreground group-hover:text-primary transition-colors mb-2">
              {category.name}
            </h3>

            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed mb-4">
              {category.description}
            </p>
          </div>

          {/* Quick Subcategory Pills */}
          {category.subcategories && category.subcategories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-5 px-6">
              {category.subcategories.slice(0, 4).map((sub) => (
                <Link
                  key={sub.slug}
                  href={getSubcategoryUrl(category.slug, sub.slug)}
                  className="text-[11px] font-medium px-2.5 py-1 rounded-lg border border-border/60 bg-muted/40 hover:bg-primary/10 hover:border-primary/40 hover:text-primary text-muted-foreground transition-all"
                >
                  {sub.name}
                </Link>
              ))}
              {subCount > 4 && (
                <span className="text-[11px] font-medium px-2 py-1 text-muted-foreground/70">
                  More
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <Link
          href={getCategoryUrl(category.slug)}
          className="inline-flex items-center justify-between w-full px-6 py-4 border-t border-border/60 bg-muted/20 group-hover:bg-primary/5 text-xs font-semibold text-primary transition-colors"
        >
          <span>{meta.cta}</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </Card>
    </motion.div>
  );
}
